import {
  Box,
  Button,
  ButtonProps,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useColorModeValue,
  useFormControl,
} from "@chakra-ui/react";
import {
  RiArrowDownSLine,
  RiBracketsLine,
  RiToggleLine,
} from "@remixicon/react";
import { forwardRef, ReactElement, useMemo } from "react";

import {
  IcoCompanyAttribute,
  IcoCompanyList,
  IcoFeatureMetric,
  IcoSegment,
  IcoUserAttribute,
  IcoUserList,
} from "@/common/components/Icons";
import { UIFilterType } from "@reflag/shared/filter";

const allTypes: readonly {
  type: UIFilterType;
  label: string;
  icon: ReactElement;
}[] = [
  {
    type: "segment",
    label: "Segment",
    icon: <IcoSegment size="sm" />,
  },
  {
    type: "companyIds",
    label: "Company list",
    icon: <IcoCompanyList size="sm" />,
  },
  {
    type: "userIds",
    label: "User list",
    icon: <IcoUserList size="sm" />,
  },
  {
    type: "companyAttribute",
    label: "Company attribute",
    icon: <IcoCompanyAttribute size="sm" />,
  },
  {
    type: "userAttribute",
    label: "User attribute",
    icon: <IcoUserAttribute size="sm" />,
  },
  {
    type: "featureMetric",
    label: "Flag metric",
    icon: <IcoFeatureMetric size="sm" />,
  },
  {
    type: "featureTargeting",
    label: "Flag access",
    icon: <RiToggleLine size={14} />,
  },
  {
    type: "otherContext",
    label: "Other context",
    icon: <RiBracketsLine size={14} />,
  },
] as const;

type RuleTypeProps = Omit<ButtonProps, "value" | "onChange"> & {
  value: UIFilterType | undefined;
  allowed?: UIFilterType[];
  onChange: (nextValue: UIFilterType) => void;
};

const RuleType = forwardRef<HTMLButtonElement, RuleTypeProps>(
  ({ allowed, value, onChange, ...rest }, ref) => {
    const formControlProps = useFormControl<HTMLButtonElement>(rest);
    const activeColor = useColorModeValue("brand.500", "brand.300");

    const usedTypes = useMemo(
      () => allTypes.filter(({ type }) => !allowed || allowed.includes(type)),
      [allowed],
    );

    const selectedOption = useMemo(() => {
      return usedTypes.find(({ type }) => type === value);
    }, [value, usedTypes]);

    return (
      <Menu size="xs">
        <MenuButton
          ref={ref}
          as={Button}
          background="appBackground"
          data-testid="rule-operator"
          isDisabled={formControlProps.disabled}
          px={3}
          rightIcon={
            <Box fontSize="xl" mr={-2}>
              <RiArrowDownSLine size={16} />
            </Box>
          }
          variant="input"
          {...formControlProps}
        >
          {selectedOption ? (
            <HStack spacing={2}>
              <Box color="dimmed" fontSize="14px">
                {selectedOption.icon}
              </Box>
              <Text display="block" noOfLines={1}>
                {selectedOption.label}
              </Text>
            </HStack>
          ) : (
            <Text color="dimmed" pl={1}>
              Filter type
            </Text>
          )}
        </MenuButton>
        <MenuList data-testid="rule-operator-list" maxH="sm" overflowY="auto">
          {usedTypes.map(({ type, label, icon }) => (
            <MenuItem
              key={type}
              color={value === type ? activeColor : undefined}
              icon={icon}
              onClick={() => onChange(type)}
            >
              {label}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    );
  },
);

RuleType.displayName = "RuleType";

export default RuleType;
